import {
  Component,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  useMemo,
} from "react";
import Select, { components, MultiValueProps, OptionTypeBase, Props } from "react-select";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  SortEndHandler,
} from "react-sortable-hoc";

export interface SelectOption {
  value: string;
  label: string;
}

export type SortableSelectProps<T extends OptionTypeBase> = Omit<
  Props<T, true>,
  "components" | "isMulti"
> & {
  onSortEnd?: SortEndHandler;
};

export const SortableSelect = forwardRef(
  <T extends SelectOption>(
    props: SortableSelectProps<T>,
    ref: ForwardedRef<Component<SortableSelectProps<T>>>
  ) => {
    const Container = useMemo(() => SortableContainer<SortableSelectProps<T>>(Select), []);
    return (
      <Container
        {...props}
        styles={{
          menu: (baseStyles: CSSProperties) => ({
            ...baseStyles,
            zIndex: 999,
          }),
        }}
        ref={ref}
        components={{
          // @ts-ignore We're failing to provide a required index prop to SortableElement
          MultiValue: SortableMultiValue,
          MultiValueLabel: SortableMultiValueLabel,
        }}
        axis="xy"
        distance={4}
        closeMenuOnSelect={false}
        isClearable={false}
        isMulti
        useDragHandle
      />
    );
  }
);

// These components should be encapsulated into a generic component, but it's a nightmare because types
const SortableMultiValue = SortableElement<MultiValueProps<SelectOption>>(
  (props: MultiValueProps<SelectOption>) => {
    // this prevents the menu from being opened/closed when the user clicks
    // on a value to begin dragging it. ideally, detecting a click (instead of
    // a drag) would still focus the control and toggle the menu, but that
    // requires some magic with refs that are out of scope for this example
    const onMouseDown: MouseEventHandler<HTMLDivElement> = e => {
      e.preventDefault();
      e.stopPropagation();
    };
    const innerProps = { ...props.innerProps, onMouseDown };
    return (
      <div style={{ zIndex: 9999 }}>
        <components.MultiValue {...props} innerProps={innerProps} />
      </div>
    );
  }
);

const SortableMultiValueLabel = SortableHandle((props: any) => (
  <components.MultiValueLabel {...props} />
));
